import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import {
    Auth,
    multiFactor,
    getAuth,
    MultiFactorInfo,
} from '@angular/fire/auth';
import { MessageService } from 'primeng/api';

@Injectable({
    providedIn: 'root',
})
export class MfaPreferencesService {
    auth: Auth = getAuth();
    private cachedFactors: MultiFactorInfo[] | null = null;

    constructor(private http: HttpClient) {}

    async checkMfaStatus(newReq: boolean = false): Promise<MultiFactorInfo[]> {
        if (this.cachedFactors && !newReq) {
            return this.cachedFactors;
        }

        try {
            const user: any = this.auth.currentUser;
            if (!user) return [];

            await user.reload(); // refresh MFA info
            const mfaUser = multiFactor(user);
            const factors = mfaUser.enrolledFactors || [];

            this.cachedFactors = factors;
            return factors;
        } catch (error) {
            console.error('Error checking MFA status:', error);
            return [];
        }
    }

    // Update primary method via callable function
    async updatePrimaryMethod(primaryMethod: string): Promise<void> {
        let body = {
            userId: await this.auth.currentUser.uid,
            primaryMethod,
        };

        let result = await this.http
            .post<any>(`${environment.pubQueueApiUrlEU}/v1/users/mfa`, body)
            .toPromise();

        if (result.success == false) {
            throw new Error(result?.error?.responseText);
        }
        return result;
    }

    // Get primary method via callable function
    async getPrimaryMethod(email?: string): Promise<string | null> {
        if (!email) {
            email = await this.auth.currentUser.email;
        }
        const result = await this.http
            .get<any>(
                `${environment.pubQueueApiUrlEU}/v1/users/mfa?email=${email}`
            )
            .toPromise();

        if (result.success == false) {
            throw new Error(result?.error?.responseText);
        }

        return result.data;
    }
    // Generate device ID
    generateDeviceId(): string {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Device fingerprint', 2, 2);
        const fingerprint = canvas.toDataURL();
        return btoa(fingerprint).substring(0, 32);
    }

    // Get device info
    getDeviceInfo() {
        return {
            browser: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenResolution: `${screen.width}x${screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }

    showMessageServiceAlert(
        messageService: MessageService,
        message: string,
        severity: string,
        summary: string
    ) {
        messageService.add({
            severity: severity,
            summary: summary,
            detail: message,
        });
        return;
    }
}
